import {
  Box,
  Tabs,
  Tab,
  HStack,
  VStack,
  TabList,
  Heading,
  Button,
  Alert,
  AlertIcon,
  AlertDescription,
  Spacer,
} from '@chakra-ui/react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { colors } from '~/packages/common-frontend/colors';
import { Breadcrumbs } from '~/packages/common-frontend/components/Breadcrumbs';
import {
  LoanAccountTag,
  useLoanAccountUrlParam,
} from '~/valleyBank/domains/LoanAccounts/hooks/LoanAccountContext';

const TABS = [
  {
    key: 'summary' as const,
    label: 'Summary',
    route: '/valley-bank/loan-accounts/summary',
  },
  {
    key: 'holdings' as const,
    label: 'Holdings',
    route: '/valley-bank/loan-accounts/holdings',
  },
];

export type LoanAccountDetailsTab = typeof TABS[number]['key'];

type LoanAccountsDetailsTabsProps = { selectedTab: LoanAccountDetailsTab };
export const LoanAccountsDetailsTabs = ({
  selectedTab,
}: LoanAccountsDetailsTabsProps) => {
  const navigate = useNavigate();
  const { generateLoanAccountUrl } = useLoanAccountUrlParam();
  return (
    <Tabs
      variant="line"
      index={TABS.findIndex(({ key }) => key === selectedTab)}
      colorScheme="blue"
      background="white"
    >
      <TabList>
        {TABS.map(({ label, route }) => (
          <Tab
            key={route}
            onClick={() => navigate(generateLoanAccountUrl(route))}
          >
            {label}
          </Tab>
        ))}
      </TabList>
    </Tabs>
  );
};

type LoanAccountsDetailsTemplateProps = {
  selectedTab: LoanAccountDetailsTab;
  refetchLoanAccount: () => void;
  displayRefetchAlert: boolean;
  headerChildren?: React.ReactNode;
  children: React.ReactNode;
};
export const LoanAccountsDetailsTemplate = ({
  selectedTab,
  refetchLoanAccount,
  displayRefetchAlert,
  children,
  headerChildren,
}: LoanAccountsDetailsTemplateProps) => {
  const { loanAccountId, generateLoanAccountUrl } = useLoanAccountUrlParam();

  const breadCrumbs = [
    { label: 'Loan Accounts', path: '/valley-bank/loan-accounts' },
  ];

  if (selectedTab === 'holdings') {
    breadCrumbs.push({
      label: 'Holdings',
      path: generateLoanAccountUrl('/valley-bank/loan-accounts/holdings'),
    });
  }

  return (
    <>
      <VStack spacing="32px" w="100%" pb="40px" alignItems="flex-start">
        <Breadcrumbs breadcrumbs={breadCrumbs} />
        <Heading mt="16px">{`Loan Account ${loanAccountId}`}</Heading>
        <HStack spacing="16px">
          {loanAccountId && (
            <LoanAccountTag loanAccountId={loanAccountId || ''} />
          )}
          {headerChildren}
        </HStack>
        {!!displayRefetchAlert && (
          <Alert status="warning">
            <AlertIcon />
            <AlertDescription fontWeight={600}>
              Changes were made to your Collateral selections.
            </AlertDescription>
            <Spacer />
            <Button
              variant="link"
              onClick={refetchLoanAccount}
              textDecoration="none"
              color={colors.info}
            >
              Update Visibility
            </Button>
          </Alert>
        )}
      </VStack>
      <LoanAccountsDetailsTabs selectedTab={selectedTab} />
      <Box w="100%" mt="40px">
        {children}
      </Box>
    </>
  );
};
